import { createSelector } from 'reselect';
import { RefundMode, RefundState, VoluntaryReason } from '../types';
import { CreateRefundParams, ExareReason, InsuranceType, NASStatus } from '@websky/graphql';
import { getCustomer } from './customer/selectors';
import { getSelectedPassengersWithChildrenIds } from './passengers/selectors';
import { getSelectedReason } from './reason/selectors';
import { getRefundFiles } from './documents/selectors';
import { getNasServices } from './refundInfo/selectors';
import { getSelectedInsurancesByTypes } from '../../utils';
import { getAviaOrderStateHash } from './aviaOrder/selectors';

export const getState = (state: RefundState) => state;

export const getOrder = createSelector(getState, state => state.aviaOrder);

export const getOrderId = createSelector(getState, state => state.aviaOrder.id);

export const getTravellers = createSelector(getOrder, order => order.travellers);

export const getRefundMode = createSelector(getState, state => state.mode);

export const isAutoRefundMode = createSelector(getRefundMode, getSelectedReason, (refundMode, reason): boolean => {
	return reason.title === VoluntaryReason.Voluntary && refundMode === RefundMode.Auto;
});
export const getIsRefundVoluntary = createSelector(getState, state => !!ExareReason[state.reason.title]);

export const getRefundInfo = createSelector(getState, state => state.refundInfo);

export const getRefundPrices = createSelector(getRefundInfo, refundInfo => refundInfo.prices);

export const getRefundSeatReleaseMode = createSelector(getState, state => state.refundInfo.seatReleaseMode);

export const createGetCreateRefundParams = (orderId: string) =>
	createSelector(
		getCustomer,
		getSelectedPassengersWithChildrenIds,
		isAutoRefundMode,
		getSelectedReason,
		getRefundFiles,
		getAviaOrderStateHash,
		getRefundInfo,
		(
			customer,
			selectedPassengersIds,
			isAutoRefundMode,
			selectedReason,
			refundFiles,
			orderStateHash,
			refundInfo
		): CreateRefundParams => {
			if (!customer) {
				return null;
			}

			const isVoluntary = selectedReason.title === VoluntaryReason.Voluntary;
			const haveUnvoluntaryReasons = refundInfo.unvoluntaryReasonsInfo?.length > 0;

			return {
				orderId: orderId,
				isCompelled: !isVoluntary && haveUnvoluntaryReasons,
				passengers: selectedPassengersIds,
				email: customer.email,
				name: customer.name,
				phone: customer.phone,
				comment: customer.comments ?? '',
				files: isVoluntary ? [] : refundFiles,
				reason: isVoluntary ? null : (selectedReason.title as ExareReason),
				orderStateHash
			};
		}
	);

export const isNasServiceRefundRequired = createSelector(
	[getNasServices],
	nas =>
		nas?.selectedServices?.some(selected =>
			selected.orders.some(order => order.status === NASStatus.Confirmed && order.isRefundable)
		) ?? false
);

export const getSelectedInsurancesByType = (insuranceType: InsuranceType) =>
	createSelector(getOrder, order =>
		getSelectedInsurancesByTypes(order?.additionalServices?.insurances?.selectedInsurances ?? [], [insuranceType])
	);

export const getGdsServicesRefundInfo = createSelector(getState, state => state.gdsServicesRefundInfo);

export const getSelectedInsurances = createSelector(
	getOrder,
	order => order.additionalServices?.insurances?.selectedInsurances
);
